import React, { useEffect, useMemo, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import { Link } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle, CheckCircle2, Clock, Calendar, Download, FileText } from "lucide-react";

const STATUS_META = {
  complete: { label: "Complete", color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  on_track: { label: "On track", color: "bg-sky-100 text-sky-800 border-sky-200", icon: Clock },
  at_risk: { label: "Renewal soon", color: "bg-amber-100 text-amber-800 border-amber-200", icon: AlertTriangle },
  overdue: { label: "Overdue", color: "bg-red-100 text-red-800 border-red-200", icon: AlertTriangle },
};

const CAT_LABEL = {
  training: "Training",
  conference: "Conference",
  teaching: "Teaching",
  publication: "Publication",
  mentoring: "Mentoring",
  self_study: "Self-study",
};

export default function CPDPage() {
  useSeo({ title: "My CPD tracker", path: "/cpd", noindex: true });
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    designation_code: "",
    activity: "",
    activity_date: new Date().toISOString().slice(0, 10),
    hours: 1,
    category: "training",
    notes: "",
  });

  const load = async () => {
    const [s, e] = await Promise.all([
      api.get("/cpd/summary"),
      api.get("/cpd/entries"),
    ]);
    setSummary(s.data);
    setEntries(e.data);
  };

  useEffect(() => { load(); }, []);

  const designations = useMemo(() => summary?.cycles?.map((c) => c.designation_code) || [], [summary]);
  useEffect(() => {
    if (!form.designation_code && designations.length) {
      setForm((f) => ({ ...f, designation_code: designations[0] }));
    }
  }, [designations]);

  const filteredEntries = useMemo(() => {
    if (!filter) return entries;
    return entries.filter((e) => e.designation_code === filter);
  }, [entries, filter]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/cpd/entries", {
        ...form,
        activity_date: new Date(form.activity_date).toISOString(),
        hours: Number(form.hours),
      });
      toast.success(`${form.hours}h logged towards ${form.designation_code}`);
      setOpen(false);
      setForm((f) => ({ ...f, activity: "", notes: "", hours: 1 }));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const removeEntry = async (id) => {
    if (!window.confirm("Delete this CPD entry?")) return;
    try {
      await api.delete(`/cpd/entries/${id}`);
      toast.success("Entry removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!summary) return <div className="min-h-[40vh] grid place-items-center text-sm text-zinc-500">Loading…</div>;

  const hasCycles = summary.cycles.length > 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <div>
          <div className="eyebrow mb-2">Continuing Professional Development</div>
          <h1 className="font-display text-4xl sm:text-5xl">CPD Tracker</h1>
          <p className="text-zinc-600 mt-2 max-w-2xl text-sm sm:text-base">
            Log activities against each CSI designation you hold. Each cycle runs for 12 months from the date of certification.
            Keep your credential in good standing by meeting the annual CPD requirement.{" "}
            <Link to="/cpd-renewal" className="font-semibold text-[var(--terracotta)] hover:underline" data-testid="cpd-renewal-info-link">CPD requirements</Link>{" · "}
            <a href="https://gcsb.uk/cpd" target="_blank" rel="noopener noreferrer" className="font-semibold text-[var(--terracotta)] hover:underline" data-testid="cpd-gcsb-submit-link">Declare at GCSB ↗</a>
          </p>
        </div>
        {hasCycles && (
          <div className="flex gap-2 flex-wrap">
            <ExportButtons designation={null} />
            <button className="btn-terracotta px-5 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2" onClick={() => setOpen(true)} data-testid="cpd-add-btn">
              <Plus size={16}/> Log CPD activity
            </button>
          </div>
        )}
      </div>

      {!hasCycles ? (
        <div className="card-warm p-10 text-center">
          <Calendar size={40} className="mx-auto text-zinc-400"/>
          <h2 className="font-display text-2xl mt-3">No active CPD cycles yet</h2>
          <p className="text-sm text-zinc-600 mt-2 max-w-md mx-auto">
            Earn a CSI designation by passing a programme's final assessment and your annual CPD cycle will start automatically.
          </p>
          <Link to="/courses" className="btn-ink inline-block mt-5 px-5 py-3 rounded-full text-sm font-semibold" data-testid="cpd-browse-btn">Browse programmes</Link>
        </div>
      ) : (
        <>
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {summary.cycles.map((c) => <CycleCard key={c.designation_code} c={c} onLog={() => { setForm((f) => ({ ...f, designation_code: c.designation_code })); setOpen(true); }} />)}
          </section>

          <section className="mt-10 card-warm p-6">
            <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
              <h2 className="font-display text-2xl">Activity log</h2>
              <div className="flex gap-2 flex-wrap">
                <button className={`chip ${!filter ? "chip-active" : ""}`} onClick={() => setFilter("")} data-testid="cpd-filter-all">All</button>
                {designations.map((d) => (
                  <button key={d} className={`chip ${filter === d ? "chip-active" : ""}`} onClick={() => setFilter(d)} data-testid={`cpd-filter-${d}`}>{d}</button>
                ))}
              </div>
            </div>

            {filteredEntries.length === 0 ? (
              <div className="py-10 text-center text-sm text-zinc-500">No entries yet. Log your first activity to get started.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border-warm)]">
                      <th className="py-2 pr-3">Date</th>
                      <th className="py-2 pr-3">Designation</th>
                      <th className="py-2 pr-3">Activity</th>
                      <th className="py-2 pr-3">Category</th>
                      <th className="py-2 pr-3 text-right">Hours</th>
                      <th className="py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEntries.map((e) => (
                      <tr key={e.id} className="border-b border-[var(--border-warm)]/60" data-testid={`cpd-entry-${e.id}`}>
                        <td className="py-3 pr-3 whitespace-nowrap">{new Date(e.activity_date).toLocaleDateString()}</td>
                        <td className="py-3 pr-3"><span className="chip">{e.designation_code}</span></td>
                        <td className="py-3 pr-3">
                          <div className="font-semibold">{e.activity}</div>
                          {e.notes && <div className="text-xs text-zinc-500 mt-0.5">{e.notes}</div>}
                        </td>
                        <td className="py-3 pr-3 text-zinc-600">{CAT_LABEL[e.category] || e.category}</td>
                        <td className="py-3 pr-3 text-right font-bold">{e.hours}h</td>
                        <td className="py-3 text-right">
                          <button onClick={() => removeEntry(e.id)} className="text-zinc-400 hover:text-red-600 p-1" data-testid={`cpd-delete-${e.id}`}>
                            <Trash2 size={14}/>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl p-6 w-full max-w-md space-y-3">
            <div className="eyebrow">New CPD activity</div>
            <h3 className="font-display text-2xl">Log activity</h3>

            <label className="block text-xs font-semibold">Designation</label>
            <select className="field" value={form.designation_code} onChange={(e) => setForm({ ...form, designation_code: e.target.value })} required data-testid="cpd-form-designation">
              {designations.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>

            <label className="block text-xs font-semibold">Activity</label>
            <input className="field" placeholder="e.g. ISACA Europe Conference 2026" value={form.activity} onChange={(e) => setForm({ ...form, activity: e.target.value })} required data-testid="cpd-form-activity"/>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold">Date</label>
                <input className="field" type="date" value={form.activity_date} onChange={(e) => setForm({ ...form, activity_date: e.target.value })} required data-testid="cpd-form-date"/>
              </div>
              <div>
                <label className="block text-xs font-semibold">Hours</label>
                <input className="field" type="number" step="0.25" min="0.25" max="100" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} required data-testid="cpd-form-hours"/>
              </div>
            </div>

            <label className="block text-xs font-semibold">Category</label>
            <select className="field" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="cpd-form-category">
              {summary.categories.map((c) => <option key={c} value={c}>{CAT_LABEL[c] || c}</option>)}
            </select>

            <label className="block text-xs font-semibold">Notes (optional)</label>
            <textarea className="field" rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cpd-form-notes"/>

            <div className="flex gap-2 pt-2">
              <button type="button" className="chip flex-1" onClick={() => setOpen(false)} data-testid="cpd-form-cancel">Cancel</button>
              <button className="btn-terracotta flex-1 py-2 rounded-full text-sm font-semibold" data-testid="cpd-form-submit">Save entry</button>
            </div>

            <style>{`
              .field { width: 100%; background: #fff; border: 1px solid var(--border-warm); border-radius: 12px; padding: 10px 14px; font-size: 14px; outline: none; }
              .field:focus { border-color: var(--ink); }
              textarea.field { resize: vertical; }
            `}</style>
          </form>
        </div>
      )}
    </div>
  );
}

function CycleCard({ c, onLog }) {
  const meta = STATUS_META[c.status];
  const Icon = meta.icon;
  const renewalDate = new Date(c.cycle_end).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  return (
    <div className="card-warm p-5" data-testid={`cpd-cycle-${c.designation_code}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="eyebrow">{c.designation_name}</div>
          <div className="font-display text-2xl mt-1">{c.designation_code}</div>
        </div>
        <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full border ${meta.color}`}>
          <Icon size={12}/> {meta.label}
        </span>
      </div>
      <div className="mt-5 flex items-center gap-4">
        <div
          className="w-20 h-20 rounded-full ring-progress grid place-items-center shrink-0"
          style={{ "--p": `${c.progress_percent}%` }}
        >
          <div className="w-16 h-16 rounded-full bg-white grid place-items-center">
            <div className="text-center leading-tight">
              <div className="font-display text-lg">{c.logged_hours}</div>
              <div className="text-[10px] text-zinc-500">/ {c.required_hours}h</div>
            </div>
          </div>
        </div>
        <div className="flex-1 text-sm">
          <div className="font-semibold">{c.remaining_hours > 0 ? `${c.remaining_hours}h to go` : "Target met!"}</div>
          <div className="text-xs text-zinc-500 mt-1">Cycle ends {renewalDate}</div>
          <div className="text-xs mt-1 font-bold" style={{ color: c.days_remaining < 60 ? "#B45309" : "#52525B" }}>
            {c.days_remaining >= 0 ? `${c.days_remaining} days remaining` : `${Math.abs(c.days_remaining)} days overdue`}
          </div>
        </div>
      </div>
      <button onClick={onLog} className="chip w-full mt-4 justify-center inline-flex" data-testid={`cpd-log-${c.designation_code}`}>
        <Plus size={14}/> Log activity for {c.designation_code}
      </button>
      <div className="mt-2 flex gap-2">
        <ExportButtons designation={c.designation_code} compact />
      </div>
    </div>
  );
}

function ExportButtons({ designation, compact }) {
  const [busy, setBusy] = React.useState(null);
  const download = async (format) => {
    setBusy(format);
    try {
      const qs = new URLSearchParams({ format });
      if (designation) qs.set("designation", designation);
      const res = await fetch(`${API}/cpd/export?${qs}`, { credentials: "include" });
      if (!res.ok) {
        const t = await res.text();
        try { throw new Error(JSON.parse(t).detail || t); } catch { throw new Error(t); }
      }
      const blob = await res.blob();
      const cd = res.headers.get("content-disposition") || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `CSI_CPD_export.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e.message || "Export failed");
    } finally { setBusy(null); }
  };
  const label = (f) => busy === f ? "…" : f.toUpperCase();
  const base = `chip inline-flex items-center gap-1 ${compact ? "flex-1 justify-center" : ""}`;
  const testIdSuffix = designation || "all";
  return (
    <>
      <button type="button" className={base} onClick={() => download("csv")} data-testid={`cpd-export-csv-${testIdSuffix}`}>
        <FileText size={13}/> {compact ? label("csv") : `Export ${label("csv")}`}
      </button>
      <button type="button" className={base} onClick={() => download("pdf")} data-testid={`cpd-export-pdf-${testIdSuffix}`}>
        <Download size={13}/> {compact ? label("pdf") : `Signed ${label("pdf")}`}
      </button>
    </>
  );
}
